import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { GenericColumnModel, BooleanColumnModel, DateColumnModel, NumberColumnModel, OptionColumnModel, OptionsColumnModel, StringColumnModel, LinkColumnModel, CurrencyColumnModel, FormatterCell, TimeColumnModel, DurationColumnModel, RemoteOptionColumnModel, TextareaColumnModel, RemoteOptionsColumnModel, ProjectOrCategoryColumnModel } from "../../models"
import { convertMinutesToTicks, convertTicksToMinutes } from "utils/dateTimeUtils"
import { StringEditableCell } from "./StringEditableCell"
import { OptionEditableCell } from "./OptionEditableCell"
import { DateEditableCell } from "./DateEditableCell"
import { NumberEditableCell } from "./NumberEditableCell"
import { BooleanEditableCell } from "./BooleanEditableCell"
import { OptionsEditableCell } from "./OptionsEditableCell"
import { LinkEditableCell } from "./LinkEditableCell"
import { TimeEditableCell } from "./TimeEditableCell"
import { RemoteOptionEditableCell } from "./RemoteOptionEditableCell"
import { TextareaEditableCell } from "./TextareaEditableCell"
import { RemoteOptionsEditableCell } from "./RemoteOptionsEditableCell"
import { ProjectOrCategoryEditableCell } from "./ProjectOrCategoryEditableCell"

type Props = {
	column: GenericColumnModel
	value: any
	onChangeComplete(columnId: string, value: any): void
	loading: boolean
	formatterCell: FormatterCell
}

const EditableCellWithoutMemo = ({ column, value, onChangeComplete, loading, formatterCell }: Props) => {
	const [currentValue, setCurrentValue] = useState(value);
	// setCurrentValueCallback and onBlur are called by AutoComplete in the same execution, so onBlur has old currentValue
	// everything about currentValueRef is a quick fix, feel free to reimplement if there is some simple solution
	const currentValueRef = useRef();
	currentValueRef.current = currentValue;

	useEffect(
		() => {
			setCurrentValue(column instanceof DurationColumnModel ? convertTicksToMinutes(value) : value);
		},
		[value, column]
	)

	const onBlurCallback = useCallback(
		() => {
			if (column instanceof DurationColumnModel) {
				const ticks = convertMinutesToTicks(currentValueRef.current);
				onChangeComplete(column.id, ticks);
			} else {
				onChangeComplete(column.id, currentValueRef.current);
			}
		},
		[column, onChangeComplete]
	)

	const setCurrentValueCallback = useCallback(
		(value: any) => {
			// for boolean column, change is completed on every change,
			// and for other columns, change is completed on blur
			if (column instanceof BooleanColumnModel) {
				currentValueRef.current = value;
				setCurrentValue(value);
				onChangeComplete(column.id, value);
			} else {
				currentValueRef.current = value;
				setCurrentValue(value)
			}
		},
		[column, onChangeComplete]
	)

	const props = useMemo(
		() => {
			return {
				value: currentValue,
				onChange: setCurrentValueCallback,
				onBlur: onBlurCallback,
				loading
			}
		},
		[currentValue, setCurrentValueCallback, onBlurCallback, loading]
	)

	if (column instanceof RemoteOptionsColumnModel) {
		return <RemoteOptionsEditableCell column={column} {...props} formatterCell={formatterCell} />
	} else if (column instanceof RemoteOptionColumnModel) {
		return <RemoteOptionEditableCell column={column} {...props} formatterCell={formatterCell} />
	} else if (column instanceof OptionColumnModel) {
		return <OptionEditableCell column={column} {...props} formatterCell={formatterCell} />
	} else if (column instanceof OptionsColumnModel) {
		return <OptionsEditableCell column={column} {...props} formatterCell={formatterCell} />
	} else if (column instanceof ProjectOrCategoryColumnModel) {
		return <ProjectOrCategoryEditableCell column={column} {...props} />
	} else if (column instanceof DateColumnModel) {
		return <DateEditableCell {...props} />
	} else if (column instanceof LinkColumnModel) {
		return <LinkEditableCell {...props} />
	} else if (column instanceof NumberColumnModel || column instanceof CurrencyColumnModel || column instanceof DurationColumnModel) {
		return <NumberEditableCell {...props} />
	} else if (column instanceof TimeColumnModel) {
		return <TimeEditableCell column={column} {...props} />
	} else if (column instanceof BooleanColumnModel) {
		return <BooleanEditableCell {...props} />
	} else if (column instanceof StringColumnModel) {
		return <StringEditableCell {...props} />
	} else if (column instanceof TextareaColumnModel) {
		return <TextareaEditableCell {...props} />
	}
	// CODE: FormattedReference is missing in editable cells, instead when there is need for editing we use Option(s)
	// so we should create some support for FormattedReference editing to make it cleaner

	return <></>
}

export const EditableCell = memo(EditableCellWithoutMemo);
